<template>
    <transition name="aloai-panel-slide">
        <div v-if="visible" class="aloai-check-panel">
            <div class="aloai-check-panel__gradient-border"></div>

            <!-- Header -->
            <div class="aloai-check-panel__header">
                <div class="d-flex align-items-center">
                    <sparkle-bold-icon
                        width="16"
                        height="16"
                        color="#9333EA"
                        class="mr-2 flex-shrink-0"
                    />
                    <span class="aloai-check-panel__title">AloAi A2P Campaign Check</span>
                </div>
                <button
                    type="button"
                    class="aloai-check-panel__close"
                    @click="$emit('dismiss')"
                >
                    <i class="material-icons">close</i>
                </button>
            </div>

            <!-- Loading state -->
            <div
                v-if="loading"
                class="aloai-check-panel__body aloai-check-panel__body--loading"
            >
                <div class="aloai-check-panel__sparkle-spin">
                    <sparkle-bold-icon width="28" height="28" color="#9333EA" />
                </div>
                <p class="aloai-check-panel__loading-text">Analyzing your campaign…</p>
                <p class="aloai-check-panel__loading-sub">
                    This may take up to a minute
                </p>
            </div>

            <!-- Sticky nav — shown whenever there are issues (outside scrollable body) -->
            <div
                v-if="result && hasIssues"
                class="aloai-check-panel__top-nav"
            >
                <button
                    type="button"
                    class="aloai-check-panel__nav-btn"
                    :disabled="currentGroupIndex === 0"
                    @click="prevIssue"
                >
                    <i class="material-icons">chevron_left</i>
                </button>

                <div class="aloai-check-panel__nav-center">
                    <span class="aloai-check-panel__progress-label">
                        {{ currentGroupIndex + 1 }}&thinsp;/&thinsp;{{ groups.length }}
                    </span>
                    <div class="aloai-check-panel__dots">
                        <button
                            v-for="(group, idx) in groups"
                            :key="group.key"
                            type="button"
                            class="aloai-check-panel__dot"
                            :class="{
                                'is-active': idx === currentGroupIndex,
                                'is-reviewed': reviewed[group.key],
                                ['is-sev-' + group.topSeverity]: true
                            }"
                            :title="group.label"
                            @click="currentGroupIndex = idx"
                        ></button>
                    </div>
                </div>

                <button
                    type="button"
                    class="aloai-check-panel__nav-btn"
                    :disabled="currentGroupIndex === groups.length - 1"
                    @click="nextIssue"
                >
                    <i class="material-icons">chevron_right</i>
                </button>
            </div>

            <!-- Results -->
            <div v-else-if="result" class="aloai-check-panel__body">
                <!-- All clear -->
                <div v-if="!hasIssues" class="aloai-check-panel__all-clear">
                    <div class="aloai-check-panel__all-clear-glow">
                        <sparkle-bold-icon width="32" height="32" color="#9333ea" />
                    </div>
                    <p class="aloai-check-panel__all-clear-text">Looking good!</p>
                    <p class="aloai-check-panel__all-clear-sub">
                        No compliance issues detected. Your campaign is ready to submit.
                    </p>
                    <div class="aloai-check-panel__all-clear-badge">
                        <i class="material-icons" style="font-size:13px;vertical-align:middle">check_circle</i>
                        Ready to submit
                    </div>
                </div>
            </div>

            <!-- Results with issues — scrollable content below sticky nav -->
            <div v-if="result && hasIssues" class="aloai-check-panel__body">
                <!-- Guided issue stepper -->
                <div class="aloai-check-panel__stepper">
                    <!-- Current group card -->
                    <transition :name="slideDirection" mode="out-in">
                        <div :key="currentGroup.key" class="aloai-check-panel__issue-card">
                            <div class="aloai-check-panel__issue-card-header">
                                <span
                                    class="aloai-check-panel__sev-badge"
                                    :class="'aloai-sev--' + currentGroup.topSeverity"
                                >
                                    {{ currentGroup.topSeverity }}
                                </span>
                                <button
                                    type="button"
                                    class="aloai-check-panel__resolve-btn"
                                    :class="{ 'is-resolved': reviewed[currentGroup.key] }"
                                    @click="markReviewed"
                                    :title="
                                        reviewed[currentGroup.key]
                                            ? 'Mark unresolved'
                                            : 'Mark as resolved'
                                    "
                                >
                                    <i class="material-icons">{{
                                        reviewed[currentGroup.key]
                                            ? 'check_circle'
                                            : 'radio_button_unchecked'
                                    }}</i>
                                    {{
                                        reviewed[currentGroup.key] ? 'Resolved' : 'Mark as resolved'
                                    }}
                                </button>
                            </div>

                            <!-- Field label -->
                            <p class="aloai-check-panel__field-label">
                                {{ currentGroup.label }}
                            </p>

                            <!-- Sub-issues list (skip synthetic fix-only issues with no codes/description) -->
                            <ul class="aloai-check-panel__sub-issues">
                                <li
                                    v-for="issue in currentGroup.issues.filter(i => (i.tcr_codes || []).length > 0 || i.description)"
                                    :key="issue.id"
                                    class="aloai-check-panel__sub-issue"
                                >
                                    <div class="aloai-check-panel__sub-issue-codes">
                                        <span
                                            v-for="code in (issue.tcr_codes || []).slice(0, 2)"
                                            :key="code"
                                            class="aloai-check-panel__tcr-code"
                                            >TCR {{ code }}</span
                                        >
                                    </div>
                                    <span>{{ issue.description }}</span>
                                </li>
                            </ul>

                            <!-- Fix suggestion box -->
                            <div
                                v-if="currentGroup.topRecommendation || currentGroup.field"
                                class="aloai-check-panel__fix-box"
                            >
                                <template v-if="currentGroup.topRecommendation">
                                    <div class="aloai-check-panel__fix-label">Suggested fix</div>
                                    <p class="aloai-check-panel__fix-text">
                                        {{ currentGroup.topRecommendation.fix }}
                                    </p>
                                </template>

                                <!-- ── CHECKBOX field ───────────────────────────────────── -->
                                <template v-if="currentGroup.fieldType === 'checkbox' || CHECKBOX_LABELS[currentGroup.field]">
                                    <div class="aloai-check-panel__checkbox-preview">
                                        <div class="aloai-check-panel__checkbox-row">
                                            <span class="aloai-check-panel__checkbox-label">Current state:</span>
                                            <span class="aloai-check-panel__checkbox-state" :class="formData[currentGroup.field] ? 'is-checked' : 'is-unchecked'">
                                                <i class="material-icons">{{ formData[currentGroup.field] ? 'check_box' : 'check_box_outline_blank' }}</i>
                                                {{ currentGroup.checkboxLabel || currentGroup.label }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="aloai-check-panel__apply-row">
                                        <el-button
                                            size="mini"
                                            :type="!formData[currentGroup.field] ? 'primary' : ''"
                                            :plain="!formData[currentGroup.field]"
                                            class="aloai-check-panel__apply-btn"
                                            :disabled="!!reviewed[currentGroup.key] || !!formData[currentGroup.field]"
                                            @click="applyCheckbox(true)"
                                        >
                                            <i class="material-icons" style="font-size:12px;vertical-align:middle">check_box</i>
                                            {{ reviewed[currentGroup.key] ? 'Applied' : 'Mark' }}
                                        </el-button>
                                        <el-button
                                            size="mini"
                                            :type="formData[currentGroup.field] ? 'primary' : ''"
                                            :plain="!!formData[currentGroup.field]"
                                            class="aloai-check-panel__apply-btn"
                                            :disabled="!!reviewed[currentGroup.key] || !formData[currentGroup.field]"
                                            @click="applyCheckbox(false)"
                                        >
                                            <i class="material-icons" style="font-size:12px;vertical-align:middle">check_box_outline_blank</i>
                                            {{ reviewed[currentGroup.key] ? 'Applied' : 'Unmark' }}
                                        </el-button>
                                        <el-button
                                            size="mini"
                                            class="aloai-check-panel__undo-btn"
                                            :disabled="!previousValues[currentGroup.key]"
                                            @click="undoTemplate"
                                        >
                                            <i class="material-icons" style="font-size:12px;vertical-align:middle">undo</i>
                                            Undo
                                        </el-button>
                                    </div>
                                </template>

                                <!-- ── TEXT / TEXTAREA field (default) ─────────────────── -->
                                <template v-else>

                                    <!-- ── Sample messages: dual block (one per sample) ──────── -->
                                    <template v-if="currentGroup.field === 'sample_message_1'">
                                        <div
                                            v-for="sampleField in ['sample_message_1', 'sample_message_2']"
                                            :key="sampleField"
                                            class="aloai-check-panel__sample-block"
                                        >
                                            <div class="aloai-check-panel__sample-block-label">
                                                {{ sampleField === 'sample_message_1' ? 'Sample Message 1' : 'Sample Message 2' }}
                                            </div>

                                            <!-- Show block when there is current text OR a suggested fix -->
                                            <div
                                                v-if="formData[sampleField] || getSuggestedFix(sampleField)"
                                                class="aloai-check-panel__example"
                                            >
                                                <template v-if="formData[sampleField]">
                                                    <div class="aloai-check-panel__example-label aloai-check-panel__example-label--original">Current text:</div>
                                                    <div class="aloai-check-panel__example-text aloai-check-panel__example-text--original">{{ formData[sampleField] }}</div>
                                                </template>
                                                <!-- Suggested text + apply row only when a fix is available -->
                                                <template v-if="getSuggestedFix(sampleField)">
                                                    <div class="aloai-check-panel__example-label">Suggested text:</div>
                                                    <div class="aloai-check-panel__example-text"><span
                                                        v-for="(span, idx) in computeDiff(formData[sampleField] || '', getSuggestedFix(sampleField))"
                                                        :key="idx"
                                                        :class="{ 'aloai-diff-new': span.isNew }"
                                                    >{{ span.text }}</span></div>
                                                    <div class="aloai-check-panel__apply-row">
                                                        <el-button
                                                            size="mini"
                                                            type="primary"
                                                            plain
                                                            class="aloai-check-panel__apply-btn"
                                                            :disabled="!!previousValues[sampleField]"
                                                            @click="applyField(sampleField)"
                                                        >
                                                            <i class="material-icons" style="font-size:12px;vertical-align:middle">auto_fix_high</i>
                                                            {{ previousValues[sampleField] ? 'Applied' : 'Apply to form' }}
                                                        </el-button>
                                                        <el-button
                                                            size="mini"
                                                            class="aloai-check-panel__undo-btn"
                                                            :disabled="!previousValues[sampleField]"
                                                            @click="undoField(sampleField)"
                                                        >
                                                            <i class="material-icons" style="font-size:12px;vertical-align:middle">undo</i>
                                                            Undo
                                                        </el-button>
                                                    </div>
                                                </template>
                                            </div>
                                        </div>
                                    </template>

                                    <!-- ── All other text/textarea fields ────────────────────── -->
                                    <template v-else>
                                        <!-- Suggested fix or example template -->
                                        <div
                                            v-if="getSuggestedFix(currentGroup.field) || currentGroup.topRecommendation?.example_template"
                                            class="aloai-check-panel__example"
                                        >
                                            <template v-if="getSuggestedFix(currentGroup.field) && formData[currentGroup.field]">
                                                <div class="aloai-check-panel__example-label aloai-check-panel__example-label--original">Current text:</div>
                                                <div class="aloai-check-panel__example-text aloai-check-panel__example-text--original">{{ formData[currentGroup.field] }}</div>
                                            </template>

                                            <div class="aloai-check-panel__example-label">{{ currentGroup.field === 'use_case' ? 'Suggested use case:' : 'Suggested text:' }}</div>
                                            <div
                                                v-if="getSuggestedFix(currentGroup.field)"
                                                class="aloai-check-panel__example-text"
                                            ><span
                                                    v-for="(span, idx) in computeDiff(formData[currentGroup.field] || '', getSuggestedFix(currentGroup.field))"
                                                    :key="idx"
                                                    :class="{ 'aloai-diff-new': span.isNew }"
                                                >{{ span.text }}</span></div>
                                            <div v-else class="aloai-check-panel__example-text">{{ currentGroup.topRecommendation?.example_template }}</div>

                                            <div class="aloai-check-panel__apply-row">
                                                <el-tooltip
                                                    :disabled="!!currentGroup.field"
                                                    content="No form field mapped for this issue"
                                                    placement="top"
                                                >
                                                    <span>
                                                        <el-button
                                                            size="mini"
                                                            type="primary"
                                                            plain
                                                            class="aloai-check-panel__apply-btn"
                                                            :disabled="!!reviewed[currentGroup.key] || !currentGroup.field"
                                                            @click="applyTemplate"
                                                        >
                                                            <i class="material-icons" style="font-size:12px;vertical-align:middle">auto_fix_high</i>
                                                            {{ reviewed[currentGroup.key] ? 'Applied' : 'Apply to form' }}
                                                        </el-button>
                                                    </span>
                                                </el-tooltip>
                                                <el-button
                                                    size="mini"
                                                    class="aloai-check-panel__undo-btn"
                                                    :disabled="!previousValues[currentGroup.key]"
                                                    @click="undoTemplate"
                                                >
                                                    <i class="material-icons" style="font-size:12px;vertical-align:middle">undo</i>
                                                    Undo
                                                </el-button>
                                            </div>
                                        </div>
                                    </template>
                                </template>
                            </div>
                        </div>
                    </transition>
                </div>
            </div>

            <!-- Error state -->
            <div
                v-if="!result && !loading && error"
                class="aloai-check-panel__body aloai-check-panel__body--error"
            >
                <i class="material-icons aloai-check-panel__error-icon">cloud_off</i>
                <p class="aloai-check-panel__error-text">
                    {{ errorMessage || 'AI check unavailable right now' }}
                </p>
                <p class="aloai-check-panel__error-sub">
                    You can still submit your campaign.
                </p>
                <el-button
                    size="small"
                    class="aloai-check-panel__retry-btn mt-2"
                    @click="$emit('retry')"
                >
                    Retry
                </el-button>
            </div>

            <!-- No footer buttons — actions are in the form itself -->
        </div>
    </transition>
</template>

<script>
import SparkleBoldIcon from '../../../../../images/app-icons/sparkle-bold-icon.vue'

// Which fields are text-like vs checkbox
const FIELD_TYPES = {
    description: 'textarea',
    message_flow: 'textarea',
    sample_message_1: 'textarea',
    sample_message_2: 'textarea',
    privacy_policy_url: 'text',
    use_case: 'text',
    has_embedded_links: 'checkbox',
    has_embedded_phone: 'checkbox',
}

const CHECKBOX_LABELS = {
    has_embedded_links: 'Messages include embedded links',
    has_embedded_phone: 'Messages include phone numbers',
}


export default {
    name: 'A2pAiCheckPanel',

    components: { SparkleBoldIcon },

    props: {
        visible: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        result: {
            type: Object,
            default: null
        },
        error: {
            type: Boolean,
            default: false
        },
        errorMessage: {
            type: String,
            default: null
        },
        formData: {
            type: Object,
            default: () => ({})
        },
    },

    data() {
        return {
            currentGroupIndex: 0,
            reviewed: {},
            slideDirection: 'aloai-slide-left',
            previousValues: {},
            CHECKBOX_LABELS,
        }
    },

    computed: {
        hasIssues() {
            return (this.result?.report?.issues?.length ?? 0) > 0
        },

        groups() {
            const issues = this.result?.report?.issues ?? []
            const recommendations = this.result?.recommendations ?? []
            const severityRank = { critical: 4, high: 3, medium: 2, low: 1 }
            const fieldLabels = {
                description: 'Campaign Description',
                message_flow: 'Opt-In / Message Flow',
                sample_message_1: 'Sample Message',
                sample_message_2: 'Sample Message 2',
                privacy_policy_url: 'Privacy Policy',
                use_case: 'Use Case',
                has_embedded_links: 'Embedded Links',
                has_embedded_phone: 'Embedded Phone Numbers',
            }

            // Bucket each issue by field key (or fall back to category)
            const buckets = {}
            issues.forEach((issue) => {
                // The validator provides aloware_field — no local TCR code mapping
                const field = issue.aloware_field ?? null
                // sample_message_2 issues are always shown in the same step as sample_message_1
                const bucketKey = field === 'sample_message_2' ? 'sample_message_1' : (field ?? issue.category ?? 'other')
                if (!buckets[bucketKey]) buckets[bucketKey] = { key: bucketKey, field: bucketKey, issues: [] }
                buckets[bucketKey].issues.push(issue)
            })

            const buildGroup = (bucket) => {
                const allTcrCodes = [
                    ...new Set(bucket.issues.flatMap((i) => i.tcr_codes ?? []))
                ]
                const topSeverity = bucket.issues.reduce((best, i) => {
                    return (severityRank[i.severity] ?? 0) > (severityRank[best] ?? 0)
                        ? i.severity
                        : best
                }, 'low')
                const topRecommendation =
                    recommendations.find((r) => allTcrCodes.includes(r.tcr_code)) ?? null
                const label =
                    fieldLabels[bucket.field] ??
                    bucket.key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
                const fieldType = FIELD_TYPES[bucket.field] ?? 'textarea'
                const checkboxLabel = CHECKBOX_LABELS[bucket.field] ?? null
                return { ...bucket, allTcrCodes, topSeverity, topRecommendation, label, fieldType, checkboxLabel }
            }

            // Sort groups in form field order
            const FIELD_ORDER = [
                'use_case',
                'description',
                'message_flow',
                'sample_message_1',
                'privacy_policy_url',
                'has_embedded_links',
                'has_embedded_phone',
            ]
            const groups = Object.values(buckets).map(buildGroup)
            groups.sort((a, b) => {
                const ai = FIELD_ORDER.indexOf(a.field ?? '')
                const bi = FIELD_ORDER.indexOf(b.field ?? '')
                const aRank = ai === -1 ? FIELD_ORDER.length : ai
                const bRank = bi === -1 ? FIELD_ORDER.length : bi
                return aRank - bRank
            })

            return groups
        },

        currentGroup() {
            return this.groups[this.currentGroupIndex] ?? null
        },

        reviewedCount() {
            return Object.values(this.reviewed).filter(Boolean).length
        },

        allResolved() {
            return this.groups.length > 0 && this.reviewedCount >= this.groups.length
        },

        progressPct() {
            if (!this.groups.length) return 0
            return Math.round((this.reviewedCount / this.groups.length) * 100)
        },

        riskLabel() {
            const labels = {
                low: 'Low Risk',
                medium: 'Medium Risk',
                high: 'High Risk',
                critical: 'Critical Risk'
            }
            return labels[this.result?.report?.overall_risk] || 'Unknown'
        },

        riskIcon() {
            const icons = {
                low: 'check_circle',
                medium: 'warning',
                high: 'error',
                critical: 'dangerous'
            }
            return icons[this.result?.report?.overall_risk] || 'help'
        },

        continueLabel() {
            if (this.loading) return 'Checking…'
            if (!this.result) return 'Continue'
            if (!this.hasIssues) return 'Looks good, continue'
            return 'Submit anyway'
        }
    },

    watch: {
        allResolved(val) {
            this.$emit('update:all-resolved', val)
        },

        result() {
            this.currentGroupIndex = 0
            this.reviewed = {}
            this.previousValues = {}
        },
    },

    methods: {
        prevIssue() {
            if (this.currentGroupIndex > 0) {
                this.slideDirection = 'aloai-slide-right'
                this.currentGroupIndex--
            }
        },

        nextIssue() {
            if (this.currentGroupIndex < this.groups.length - 1) {
                this.slideDirection = 'aloai-slide-left'
                this.currentGroupIndex++
            }
        },

        markReviewed() {
            const key = this.currentGroup?.key
            if (!key) return
            this.$set(this.reviewed, key, !this.reviewed[key])
        },

        applyTemplate() {
            const field = this.currentGroup?.field
            const text =
                this.getSuggestedFix(field) ||
                this.currentGroup?.topRecommendation?.example_template
            if (!field || !text) return
            this.$set(this.previousValues, this.currentGroup.key, { field, value: this.formData[field] ?? '' })
            this.$emit('apply-fix', { field, value: text })
            this.$set(this.reviewed, this.currentGroup.key, true)
        },

        undoTemplate() {
            const prev = this.previousValues[this.currentGroup?.key]
            if (!prev) return
            this.$emit('undo-fix', { field: prev.field, value: prev.value })
            this.$set(this.previousValues, this.currentGroup.key, null)
            this.$set(this.reviewed, this.currentGroup.key, false)
        },

        applyField(field) {
            const text = this.getSuggestedFix(field)
            if (!field || !text) return
            this.$set(this.previousValues, field, { field, value: this.formData[field] ?? '' })
            this.$emit('apply-fix', { field, value: text })
            this.$set(this.reviewed, this.currentGroup.key, true)
        },

        undoField(field) {
            const prev = this.previousValues[field]
            if (!prev) return
            this.$emit('undo-fix', { field: prev.field, value: prev.value })
            this.$set(this.previousValues, field, null)
            const otherSample = field === 'sample_message_1' ? 'sample_message_2' : 'sample_message_1'
            if (!this.previousValues[otherSample]) {
                this.$set(this.reviewed, this.currentGroup.key, false)
            }
        },

        applyCheckbox(value) {
            const field = this.currentGroup?.field
            if (!field) return
            this.$set(this.previousValues, this.currentGroup.key, {
                field,
                value: this.formData[field] ?? false,
            })
            this.$emit('apply-fix', { field, value })
            this.$set(this.reviewed, this.currentGroup.key, true)
        },

        computeDiff(original, generated) {
            // Tokenize preserving whitespace so we can reconstruct the string exactly
            const origTokens = original.match(/\S+|\s+/g) || []
            const genTokens = generated.match(/\S+|\s+/g) || []
            const m = origTokens.length
            const n = genTokens.length
            // LCS table
            const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0))
            for (let i = 1; i <= m; i++) {
                for (let j = 1; j <= n; j++) {
                    dp[i][j] =
                        origTokens[i - 1] === genTokens[j - 1]
                            ? dp[i - 1][j - 1] + 1
                            : Math.max(dp[i - 1][j], dp[i][j - 1])
                }
            }
            // Traceback
            const spans = []
            let i = m,
                j = n
            while (i > 0 || j > 0) {
                if (i > 0 && j > 0 && origTokens[i - 1] === genTokens[j - 1]) {
                    spans.unshift({ text: genTokens[j - 1], isNew: false })
                    i--
                    j--
                } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
                    const isSpace = /^\s+$/.test(genTokens[j - 1])
                    spans.unshift({ text: genTokens[j - 1], isNew: !isSpace })
                    j--
                } else {
                    i--
                }
            }
            return spans
        },

        getSuggestedFix(field) {
            if (!field) return null
            const issues = this.currentGroup?.issues ?? []
            // Find first issue whose aloware_field matches, or whose field matches the group field
            for (const issue of issues) {
                if (issue.suggested_fix && (issue.aloware_field === field || (!issue.aloware_field && field === this.currentGroup?.field))) {
                    return issue.suggested_fix.trim()
                }
            }
            // Fall back: any issue in the group with a suggested_fix for this field
            if (field === this.currentGroup?.field) {
                const fix = issues.find((i) => i.suggested_fix)?.suggested_fix
                return fix ? fix.trim() : null
            }
            return null
        },
    }
}
</script>

<style scoped>
.aloai-check-panel {
    position: fixed;
    top: 50%;
    right: 32px;
    transform: translateY(-50%);
    width: 420px;
    background: #fff;
    border-radius: 12px;
    box-shadow:
        0 8px 32px rgba(147, 51, 234, 0.15),
        0 2px 8px rgba(0, 0, 0, 0.08);
    z-index: 9000;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    border: 1px solid rgba(147, 51, 234, 0.2);
}

/* Animated purple top border */
.aloai-check-panel__gradient-border {
    height: 3px;
    background: linear-gradient(90deg, #9333ea, #6366f1, #9333ea);
    background-size: 200% 100%;
    animation: aloai-gradient-scroll 2s linear infinite;
}

@keyframes aloai-gradient-scroll {
    0% {
        background-position: 0% 0%;
    }
    100% {
        background-position: 200% 0%;
    }
}

/* Slide-in transition */
.aloai-panel-slide-enter-active,
.aloai-panel-slide-leave-active {
    transition:
        opacity 0.3s ease,
        transform 0.3s ease;
}
.aloai-panel-slide-enter,
.aloai-panel-slide-leave-to {
    opacity: 0;
    transform: translateY(-50%) translateX(40px);
}

/* Issue card slide transitions */
.aloai-slide-left-enter-active,
.aloai-slide-left-leave-active,
.aloai-slide-right-enter-active,
.aloai-slide-right-leave-active {
    transition:
        opacity 0.18s ease,
        transform 0.18s ease;
}
.aloai-slide-left-enter {
    opacity: 0;
    transform: translateX(24px);
}
.aloai-slide-left-leave-to {
    opacity: 0;
    transform: translateX(-24px);
}
.aloai-slide-right-enter {
    opacity: 0;
    transform: translateX(-24px);
}
.aloai-slide-right-leave-to {
    opacity: 0;
    transform: translateX(24px);
}

/* Header */
.aloai-check-panel__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px;
    border-bottom: 1px solid rgba(147, 51, 234, 0.08);
    background: rgba(147, 51, 234, 0.03);
}

.aloai-check-panel__title {
    font-size: 13px;
    font-weight: 700;
    background: linear-gradient(135deg, #9333ea, #6366f1);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.aloai-check-panel__close {
    background: none;
    border: none;
    cursor: pointer;
    color: #9ca3af;
    padding: 2px;
    line-height: 1;
    display: flex;
}
.aloai-check-panel__close .material-icons {
    font-size: 18px;
}
.aloai-check-panel__close:hover {
    color: #374151;
}

/* Body */
.aloai-check-panel__body {
    padding: 14px;
    overflow-y: auto;
    max-height: 560px;
    flex: 1;
}
.aloai-check-panel__body--loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 14px;
}
.aloai-check-panel__body--error {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 28px 14px;
    text-align: center;
}

/* Loading animation */
.aloai-check-panel__sparkle-spin {
    animation: aloai-pulse 1.4s ease-in-out infinite;
}
@keyframes aloai-pulse {
    0%,
    100% {
        transform: scale(1);
        opacity: 1;
    }
    50% {
        transform: scale(1.15);
        opacity: 0.7;
    }
}
.aloai-check-panel__loading-text {
    margin: 12px 0 4px;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
}
.aloai-check-panel__loading-sub {
    font-size: 11px;
    color: #9ca3af;
    margin: 0;
}

/* Summary row */
.aloai-check-panel__summary-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    flex-wrap: wrap;
}

.aloai-check-panel__risk-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.aloai-check-panel__risk-icon {
    font-size: 13px;
}
.aloai-risk--low {
    background: #dcfce7;
    color: #16a34a;
}
.aloai-risk--medium {
    background: #fef9c3;
    color: #b45309;
}
.aloai-risk--high {
    background: #ffedd5;
    color: #c2410c;
}
.aloai-risk--critical {
    background: #fee2e2;
    color: #b91c1c;
}

/* All clear */
.aloai-check-panel__all-clear {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 32px 24px;
    gap: 10px;
}
.aloai-check-panel__all-clear-glow {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(147, 51, 234, 0.12), rgba(147, 51, 234, 0.04));
    border: 1.5px solid rgba(147, 51, 234, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 4px;
    box-shadow: 0 0 24px rgba(147, 51, 234, 0.12);
}
.aloai-check-panel__all-clear-text {
    font-size: 16px;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
    letter-spacing: -0.01em;
}
.aloai-check-panel__all-clear-sub {
    font-size: 12px;
    color: #6b7280;
    margin: 0;
    line-height: 1.5;
    max-width: 260px;
}
.aloai-check-panel__all-clear-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    color: #16a34a;
    font-size: 11px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 20px;
    margin-top: 4px;
}

/* Top nav row — sticky strip outside the scroll area */
.aloai-check-panel__top-nav {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border-bottom: 1px solid rgba(147, 51, 234, 0.08);
    background: rgba(147, 51, 234, 0.02);
    flex-shrink: 0;
}

.aloai-check-panel__nav-btn {
    flex-shrink: 0;
    width: 26px;
    height: 26px;
    border: none;
    background: none;
    border-radius: 6px;
    cursor: pointer;
    color: #9ca3af;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition:
        background 0.15s,
        color 0.15s;
}
.aloai-check-panel__nav-btn .material-icons {
    font-size: 18px;
}
.aloai-check-panel__nav-btn:hover:not(:disabled) {
    background: #f3f4f6;
    color: #374151;
}
.aloai-check-panel__nav-btn:disabled {
    opacity: 0.3;
    cursor: default;
}

.aloai-check-panel__nav-center {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
}

.aloai-check-panel__progress-label {
    font-size: 10px;
    color: #9ca3af;
    line-height: 1;
}

/* Dots nav */
.aloai-check-panel__dots {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
}
.aloai-check-panel__dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: none;
    cursor: pointer;
    opacity: 0.35;
    transition:
        opacity 0.15s,
        transform 0.15s;
    padding: 0;
}
.aloai-check-panel__dot.is-active {
    opacity: 1;
    transform: scale(1.35);
}
.aloai-check-panel__dot.is-reviewed {
    background: #16a34a !important;
    opacity: 0.75;
}
.aloai-check-panel__dot.is-sev-critical {
    background: #dc2626;
}
.aloai-check-panel__dot.is-sev-high {
    background: #f97316;
}
.aloai-check-panel__dot.is-sev-medium {
    background: #eab308;
}
.aloai-check-panel__dot.is-sev-low {
    background: #16a34a;
}

/* Issue card */
.aloai-check-panel__issue-card {
    background: #fafafa;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 10px;
}

.aloai-check-panel__issue-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
}

.aloai-check-panel__sev-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
}
.aloai-sev--critical {
    background: #fee2e2;
    color: #b91c1c;
}
.aloai-sev--high {
    background: #ffedd5;
    color: #c2410c;
}
.aloai-sev--medium {
    background: #fef9c3;
    color: #b45309;
}
.aloai-sev--low {
    background: #dcfce7;
    color: #16a34a;
}

.aloai-check-panel__resolve-btn {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 11px;
    font-weight: 600;
    color: #9ca3af;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 2px 4px;
    border-radius: 4px;
    transition:
        color 0.15s,
        background 0.15s;
    line-height: 1;
}
.aloai-check-panel__resolve-btn .material-icons {
    font-size: 13px;
}
.aloai-check-panel__resolve-btn:hover {
    background: #f0fdf4;
    color: #16a34a;
}
.aloai-check-panel__resolve-btn.is-resolved {
    color: #16a34a;
}

.aloai-check-panel__field-label {
    font-size: 13px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 8px;
    line-height: 1.4;
}

.aloai-check-panel__sub-issues {
    list-style: none;
    padding: 0;
    margin: 0 0 10px;
}
.aloai-check-panel__sub-issue {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: 12px;
    color: #4b5563;
    line-height: 1.5;
    padding-bottom: 8px;
    margin-bottom: 8px;
    border-bottom: 1px solid #e5e7eb;
}
.aloai-check-panel__sub-issue:last-child {
    border-bottom: none;
    padding-bottom: 0;
    margin-bottom: 0;
}

.aloai-check-panel__sub-issue-codes {
    flex-shrink: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
    padding-top: 1px;
}

/* Fix box */
.aloai-check-panel__fix-box {
    background: linear-gradient(
        135deg,
        rgba(147, 51, 234, 0.04),
        rgba(99, 102, 241, 0.04)
    );
    border: 1px solid rgba(147, 51, 234, 0.15);
    border-radius: 6px;
    padding: 10px;
    margin-bottom: 8px;
}
.aloai-check-panel__fix-label {
    font-size: 11px;
    font-weight: 700;
    color: #9333ea;
    margin-bottom: 5px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.aloai-check-panel__fix-text {
    font-size: 12px;
    color: #374151;
    margin: 0 0 8px;
    line-height: 1.5;
}
.aloai-check-panel__fix-text:last-child {
    margin-bottom: 0;
}

.aloai-check-panel__example {
    border-top: 1px solid rgba(147, 51, 234, 0.1);
    padding-top: 8px;
    margin-top: 4px;
}
.aloai-check-panel__example-label {
    font-size: 10px;
    font-weight: 700;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin: 0 0 4px;
    padding: 0;
}
.aloai-check-panel__example-text {
    font-size: 11px;
    color: #4b5563;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    padding: 6px 8px;
    margin: 0 0 8px;
    line-height: 1.5;
    font-style: italic;
    white-space: pre-line;
    word-break: break-word;
}
.aloai-check-panel__example-text--original {
    color: #9ca3af;
    background: #f9fafb;
    border-color: #f3f4f6;
    margin-bottom: 4px;
    font-style: normal;
    white-space: normal;
}
.aloai-check-panel__example-label--original {
    color: #9ca3af;
}
.aloai-diff-new {
    color: #7c3aed;
    text-decoration: underline;
    text-decoration-color: rgba(124, 58, 237, 0.4);
    text-underline-offset: 2px;
    font-weight: 500;
}
.aloai-check-panel__apply-row {
    display: flex;
    gap: 6px;
}
.aloai-check-panel__apply-btn {
    flex: 1;
}
.aloai-check-panel__undo-btn {
    flex-shrink: 0;
    color: #6b7280 !important;
    border-color: #d1d5db !important;
}
.aloai-check-panel__undo-btn:hover {
    color: #374151 !important;
    background: #f3f4f6 !important;
}

/* Generate suggestion row */
.aloai-check-panel__sample-block {
    border-top: 1px solid rgba(147, 51, 234, 0.08);
    padding-top: 8px;
    margin-top: 8px;
}
.aloai-check-panel__sample-block:first-child {
    border-top: none;
    margin-top: 0;
    padding-top: 0;
}
.aloai-check-panel__sample-block-label {
    font-size: 10px;
    font-weight: 700;
    color: #9333ea;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 6px;
}
.aloai-check-panel__generate-row {
    border-top: 1px solid rgba(147, 51, 234, 0.1);
    padding-top: 8px;
    margin-top: 4px;
}
.aloai-check-panel__generate-btn {
    width: 100%;
    border-color: rgba(147, 51, 234, 0.3) !important;
    color: #9333ea !important;
    display: flex;
    align-items: center;
    justify-content: center;
}
.aloai-check-panel__generate-btn:hover {
    background: rgba(147, 51, 234, 0.05) !important;
}

/* Checkbox preview */
.aloai-check-panel__checkbox-preview {
    margin-bottom: 8px;
}
.aloai-check-panel__checkbox-row {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
}
.aloai-check-panel__checkbox-label {
    color: #9ca3af;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    flex-shrink: 0;
}
.aloai-check-panel__checkbox-state {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 500;
}
.aloai-check-panel__checkbox-state .material-icons { font-size: 16px; }
.aloai-check-panel__checkbox-state.is-checked { color: #16a34a; }
.aloai-check-panel__checkbox-state.is-unchecked { color: #9ca3af; }

/* Radio group */
.aloai-check-panel__radio-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 8px;
    max-height: 180px;
    overflow-y: auto;
}
.aloai-check-panel__radio-option {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #374151;
    padding: 4px 6px;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.1s;
}
.aloai-check-panel__radio-option:hover { background: #f3f4f6; }
.aloai-check-panel__radio-option.is-current { color: #6b7280; }
.aloai-check-panel__radio-option.is-selected { background: rgba(147, 51, 234, 0.06); font-weight: 600; color: #7c3aed; }
.aloai-check-panel__radio-option input[type='radio'] { accent-color: #9333ea; flex-shrink: 0; }
.aloai-check-panel__radio-tag {
    margin-left: auto;
    font-size: 9px;
    font-weight: 700;
    padding: 1px 5px;
    border-radius: 3px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    flex-shrink: 0;
}
.aloai-check-panel__radio-tag--current { background: #e5e7eb; color: #6b7280; }
.aloai-check-panel__radio-tag--ai { background: rgba(147,51,234,0.12); color: #7c3aed; }

/* TCR codes */
.aloai-check-panel__tcr-codes {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}
.aloai-check-panel__tcr-code {
    font-size: 10px;
    color: #b91c1c;
    background: #fff1f2;
    border: 1px solid #fecdd3;
    border-radius: 3px;
    padding: 1px 5px;
    opacity: 0.75;
}

/* Error */
.aloai-check-panel__error-icon {
    font-size: 32px;
    color: #d1d5db;
}
.aloai-check-panel__error-text {
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    margin: 8px 0 4px;
}
.aloai-check-panel__error-sub {
    font-size: 11px;
    color: #9ca3af;
    margin: 0;
}
</style>
